import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { Config } from '@/config';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  deleteModalRecoil,
  eventDetailModalRecoil,
} from '@/atom/ModalAtom';
import { EventControllerGetEventsParams } from '@/sdk/data-contracts';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import TitleTextBox from '@/component/TitleTextBox';
import Pagenation from '@/component/Pagenation';
import SearchBtn from '@/component/SearchBtn';
import EventDetailModal from '@/modal/EventDetailModal';
import { EventApi } from '@/sdk/EventApi';
import { eventListRecoil, eventDetailIdRecoil } from '@/atom/EventAtom';
import { EventReceptions } from '@/sdk/data-contracts';

const Event = () => {
  const eventApi = new EventApi({ baseURL: Config.API_URL });
  const navigate = useNavigate();

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const [deleteModalState, setDeleteModalState] =
    useRecoilState(deleteModalRecoil);
  const [eventListState, setEventListState] = useRecoilState(eventListRecoil);
  const [eventDetailModalState, setEventDetailModalState] = useRecoilState(
    eventDetailModalRecoil,
  );
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [eventName, setEventName] = useState<string>('');
  const [deviceName, setDeviceName] = useState<string>('');
  const [deviceNumber, setDeviceNumber] = useState<string>('');
  const [deviceDevEUI, setDeviceDevEUI] = useState<string>('');
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<string>('desc');
  const [listLength, setListLength] = useState<number>(0);
  const [modifyNum, setModifyNum] = useState<number>(0);

  const downloadCSV = async (saveFileName: string) => {
    let allList: EventReceptions[] | undefined;

    const dto: EventControllerGetEventsParams = {
      sortBy,
      order,
    };
    try {
      const { data } = await eventApi.eventControllerGetEvents(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      if (data.events) allList = data.events;
    } catch (err: any) {}

    if (!allList || allList.length === 0) {
      setErrorModalText('다운로드할 데이터가 없습니다.');
      return;
    }

    const headers = [
      'No',
      '이벤트 발생 일시',
      '이벤트명',
      '이벤트 조건 데이터',
      '디바이스 No',
      'Dev EUI',
      '디바이스 명',
      '시설명',
    ];

    const rows = allList.map((list, index) => [
      index + 1,
      list.eventDate,
      list.name,
      list.eventConditionData,
      list.deviceNumber,
      list.deviceDevEUI,
      list.deviceName,
      list.facilityName,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n');

    const bom = '\uFEFF';
    const csvWithBom = bom + csvContent;

    const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${saveFileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filterEventList = async () => {
    const dto: EventControllerGetEventsParams = {
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      eventName,
      deviceName,
      deviceNumber,
      deviceDevEUI,
      offset,
      limit,
      sortBy,
      order,
    };
    try {
      const { data } = await eventApi.eventControllerGetEvents(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      if (data.events) setEventListState(data.events);
      if (data.totalColumn) {
        setListLength(data.totalColumn);
      } else if (data.totalColumn === 0) {
        setListLength(0);
      }
    } catch (err: any) {}
  };

  const handleDateRangeChange = (days: number) => {
    const today = new Date();
    const calculatedStartDate = new Date(today);
    calculatedStartDate.setDate(today.getDate() - days);

    setStartDate(calculatedStartDate.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    filterEventList();
    setOffset(0);
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLimit(Number(e.target.value));
    setOffset(0);
  };

  const handleOffsetChange = (value: number) => {
    setOffset((value - 1) * limit);
  };

  useEffect(() => {
    filterEventList();
  }, [limit, offset]);

  return (
    <>
      <CntBox1>
        <TitleTextBox text="이벤트" />
        <form onSubmit={handleSearch}>
          <SearchOptionBox>
            <InputWrap>
              <InputArea>
                <h2>이벤트 발생 일시</h2>
                <IS.TextInputType1
                  style={{ width: 145 }}
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    if (endDate !== '') {
                      if (e.target.value > endDate) {
                        setErrorModalText(
                          '시작일은 종료일보다 클 수 없습니다.',
                        );
                        return;
                      }
                    }
                    setStartDate(e.target.value);
                  }}
                />
                <h2>~</h2>
                <IS.TextInputType1
                  style={{ width: 145 }}
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    if (startDate !== '') {
                      if (e.target.value < startDate) {
                        setErrorModalText(
                          '종료일은 시작일보다 클 수 없습니다.',
                        );
                        return;
                      }
                    }
                    setEndDate(e.target.value);
                  }}
                />
                <CheckWrap>
                  <IS.CheckTextBoxType2>
                    <input
                      type="radio"
                      id="week"
                      name="date"
                      onChange={() => handleDateRangeChange(7)}
                    />
                    <label htmlFor="week">
                      <span className="img-box">
                        <img src="/img/ico_check_3.svg" alt="" />
                      </span>
                      <span className="text">1주일</span>
                    </label>
                  </IS.CheckTextBoxType2>
                  <IS.CheckTextBoxType2>
                    <input
                      type="radio"
                      id="month"
                      name="date"
                      onChange={() => handleDateRangeChange(30)}
                    />
                    <label htmlFor="month">
                      <span className="img-box">
                        <img src="/img/ico_check_3.svg" alt="" />
                      </span>
                      <span className="text">1개월</span>
                    </label>
                  </IS.CheckTextBoxType2>
                  <IS.CheckTextBoxType2>
                    <input
                      type="radio"
                      id="month3"
                      name="date"
                      onChange={() => handleDateRangeChange(90)}
                    />
                    <label htmlFor="month3">
                      <span className="img-box">
                        <img src="/img/ico_check_3.svg" alt="" />
                      </span>
                      <span className="text">3개월</span>
                    </label>
                  </IS.CheckTextBoxType2>
                  <IS.CheckTextBoxType2>
                    <input
                      type="radio"
                      id="month6"
                      name="date"
                      onChange={() => handleDateRangeChange(180)}
                    />
                    <label htmlFor="month6">
                      <span className="img-box">
                        <img src="/img/ico_check_3.svg" alt="" />
                      </span>
                      <span className="text">6개월</span>
                    </label>
                  </IS.CheckTextBoxType2>
                </CheckWrap>
              </InputArea>
              <InputArea>
                <IS.TextInputType1
                  style={{ width: 153 }}
                  type="text"
                  placeholder="디바이스명"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                />
                <IS.TextInputType1
                  style={{ width: 320 }}
                  type="text"
                  placeholder="디바이스 No"
                  value={deviceNumber}
                  onChange={(e) => setDeviceNumber(e.target.value)}
                />
                <IS.TextInputType1
                  style={{ width: 290 }}
                  type="text"
                  placeholder="Dev EUI"
                  value={deviceDevEUI}
                  onChange={(e) => setDeviceDevEUI(e.target.value)}
                />
                <IS.TextInputType1
                  style={{ width: 290 }}
                  type="text"
                  placeholder="이벤트 명"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                />
              </InputArea>
            </InputWrap>
            <SearchBtn />
          </SearchOptionBox>
        </form>
        <TableOpitonBox>
          <h1>
            전체 <span>{listLength.toLocaleString()}</span>
          </h1>
          <div className="right-box">
            <IS.SelectType1>
              <select
                value={limit}
                onChange={handleLimitChange}
                className="selected"
                style={{ width: 153 }}
              >
                <option value={10}>10개씩 보기</option>
                <option value={20}>20개씩 보기</option>
                <option value={30}>30개씩 보기</option>
                <option value={50}>50개씩 보기</option>
              </select>
              <span className="img-box">
                <img src="/img/arrow_bottom_555.svg" alt="" />
              </span>
            </IS.SelectType1>
            <BS.Btn1Type5
              type="button"
              onClick={() => downloadCSV('event_list')}
            >
              Excel 다운로드
              <span className="img-box">
                <img src="/img/ico_down_fff.svg" alt="" />
              </span>
            </BS.Btn1Type5>
          </div>
        </TableOpitonBox>
        <TableBox>
          <table>
            <thead>
              <tr>
                <th>No</th>
                <th>이벤트 발생 일시</th>
                <th>이벤트명</th>
                <th>이벤트 조건 데이터</th>
                <th>디바이스 No</th>
                <th>Dev EUI</th>
                <th>디바이스 명</th>
                <th>시설명</th>
              </tr>
            </thead>
            <tbody>
              {eventListState?.map((list, index) => (
                <tr key={index}>
                  <td>{(offset + index + 1).toLocaleString()}</td>
                  <td>{list.eventDate}</td>
                  <td
                    className="button-text"
                    onClick={() => {
                      setModifyNum(list.id);
                      setEventDetailModalState(true);
                    }}
                  >
                    {list.name}
                  </td>
                  <td>{list.eventConditionData}</td>
                  <td>{list.deviceNumber}</td>
                  <td>{list.deviceDevEUI}</td>
                  <td>{list.deviceName}</td>
                  <td>{list.facilityName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableBox>
        {listLength !== 0 && (
          <Pagenation
            listAllLength={Number(listLength)}
            listLimit={limit}
            onCurrentPageChange={handleOffsetChange}
          />
        )}
      </CntBox1>
      {eventDetailModalState && <EventDetailModal modifyNum={modifyNum} />}
    </>
  );
};

export default Event;

export const CntBox1 = styled(CS.CntBoxType1)`
  width: 100%;
`;

export const SearchOptionBox = styled(CS.SearchOptionBox)`
  width: 100%;
  margin-top: 20px;
`;

export const InputWrap = styled.div`
  gap: 8px;
  display: flex;
  flex-direction: column;
`;

export const InputArea = styled.div`
  gap: 8px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  > h2 {
    font-size: 14px;
    color: #555;
  }
`;

export const ReportBox = styled.div`
  margin-top: 40px;
  display: flex;
  align-items: center;
  gap: 8px;
  > h2 {
    font-size: 14px;
    color: #555;
  }
`;

export const TableOpitonBox = styled(CS.TableOpitonBox)`
  width: 100%;
  margin-top: 20px;
`;

export const TableBox = styled(CS.TableType1)`
  width: 100%;
  margin-top: 16px;
  margin-bottom: 32px;
`;

export const CheckWrap = styled(CS.Gap24Box)`
  margin-left: 16px;
`;
